/**
 * Etiquetas y tonos (ui-v2.5 `Badge`) de los estados del módulo, en UN sitio.
 *
 * Desde el 3/11 (B2) las usan también el panel del técnico, el resumen (SeccionCitas), el calendario
 * (PanelDia) y el dashboard (BloqueProximasCitas), que llevaban cada uno su copia con las variantes
 * de ui-v2.
 *
 * Los sufijos difieren a propósito entre participación (`cancelado_*`, `realizado`) y cita
 * (`cancelada_*`, `realizada`): son dos ENUM distintos en la base.
 *
 * Las seis de cita se distinguen: reservada y confirmada ya no comparten tono, y las canceladas
 * dicen por quién. Contraste texto/fondo de los seis tonos v2.5, medido el 3/11: de 6,38 a 7,57 (AA
 * pide 4,5). Una tarjeta cancelada se atenúa con fondo y borde apagados, NUNCA con `opacity`: a 0,6
 * el contraste de la insignia cae a 2,7–3,1.
 */

export const ESTADO_CITA = {
  reservada: { label: 'Reservada', tone: 'progress' },
  confirmada: { label: 'Confirmada', tone: 'received' },
  realizada: { label: 'Realizada', tone: 'active' },
  no_asistio: { label: 'No asistió', tone: 'waiting' },
  cancelada_usuario: { label: 'Cancelada · usuario', tone: 'expired' },
  cancelada_sistemas: { label: 'Cancelada · sistemas', tone: 'expired' },
};

/** Una cita abierta: se puede guardar progreso, cerrar o cancelar. */
export const CITA_ABIERTA = ['reservada', 'confirmada'];
export const esCitaAbierta = (estado) => CITA_ABIERTA.includes(estado);
export const esCitaCancelada = (estado) => estado === 'cancelada_usuario' || estado === 'cancelada_sistemas';

/**
 * El motivo de una cita, dicho según su estado (`motivo_cierre` guarda los dos desde el 3/11): por
 * qué se cerró con subtareas sin hacer, o por qué se canceló / no asistió. Texto plano; null si no
 * hay. `progreso` = { hechas, total }, para «Cerrada con N/M».
 */
export function textoMotivo(cita, progreso = null) {
  if (!cita || !cita.motivo_cierre) return null;
  const m = cita.motivo_cierre;
  switch (cita.estado) {
    case 'realizada': return progreso ? `Cerrada con ${progreso.hechas}/${progreso.total}: ${m}` : `Cerrada: ${m}`;
    case 'cancelada_usuario': return `Cancelada por el usuario: ${m}`;
    case 'cancelada_sistemas': return `Cancelada por sistemas: ${m}`;
    case 'no_asistio': return `No asistió: ${m}`;
    default: return null;
  }
}

/**
 * Para la ficha (3/11): quién canceló la cita o la marcó «no asistió», y el motivo. `cita.cancelacion`
 * viene del historial ({ por_la_propia_persona, realizado_por: { nombre } | null, motivo }); antes del
 * 3/11 la cancelación de la propia persona y la de gestión no dejaban historial: «no consta».
 * null si la cita no está cancelada ni es «no asistió».
 */
export function detalleCancelacion(cita) {
  if (!cita || !(esCitaCancelada(cita.estado) || cita.estado === 'no_asistio')) return null;
  const c = cita.cancelacion;
  const quien = !c ? 'Quién: no consta'
    : c.por_la_propia_persona ? 'La canceló la propia persona'
      : c.realizado_por ? `${cita.estado === 'no_asistio' ? 'Lo marcó' : 'La canceló'} ${c.realizado_por.nombre || `la persona #${c.realizado_por.id}`}`
        : 'Quién: no consta';
  const motivo = cita.motivo || (c && c.motivo) || null;
  return motivo ? `${quien} · Motivo: ${motivo}` : quien;
}

export const ESTADO_PARTICIPACION = {
  pendiente_reserva: { label: 'Sin cita', tone: 'waiting' },
  cita_reservada: { label: 'Cita reservada', tone: 'progress' },
  realizado: { label: 'Realizada', tone: 'active' },
  cancelado_usuario: { label: 'Cancelada · usuario', tone: 'expired' },
  cancelado_sistemas: { label: 'Cancelada · sistemas', tone: 'expired' },
};

export const ESTADO_CAMPANA = {
  Borrador: { label: 'Borrador', tone: 'info' },
  Activa: { label: 'Activa', tone: 'active' },
  Pausada: { label: 'Pausada', tone: 'waiting' },
  Completada: { label: 'Completada', tone: 'received' },
  Cancelada: { label: 'Cancelada', tone: 'expired' },
};

/**
 * Cómo se hizo la intervención (citas_tecnicas.modalidad, 24/10). Una registrada sin cita es
 * presencial, teléfono, remoto o Teams, y no tiene hora. Mismos textos que el CSV y el xlsx del servidor.
 */
export const MODALIDAD = {
  con_cita: { label: 'Con cita', tone: 'info' },
  presencial: { label: 'Presencial', tone: 'received' },
  telefono: { label: 'Teléfono', tone: 'received' },
  remoto: { label: 'Remoto', tone: 'received' },
  teams: { label: 'Teams', tone: 'received' },
};
/** Las que se registran sin cita, en el orden del selector. Las mismas que el ENUM del servidor. */
export const MODALIDADES_SIN_CITA = ['presencial', 'telefono', 'remoto', 'teams'];

/** La etiqueta de un estado; si no se conoce, el valor tal cual (mejor verlo raro que no verlo). */
export const etiquetaDe = (mapa, estado) => mapa[estado] || { label: estado || '—', tone: 'info' };
